import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { formatDateTime } from "@tellescope/utils";
import { FormAnswer } from "./FormAnswer";
import { Button, cn, DestructiveConfirmDialog } from "@tellescope/ui";
import { FLOATING_THUMB_CLASS, SCROLL_SURFACE_CLASS, scrollSurfaceStyle } from "@tellescope/react-hooks";
import { useScrollSurface } from "./useScrollSurface";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ClipboardPlus, FileText, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { SinglePageFormSubmission } from "./SinglePageFormSubmission";
import { BlockEmptyState } from "../blocks/BlockEmptyState";

type Response = TellescopeModelFromName<"form_responses">;

export interface FormResponseProps {
  timestamp: string | Date;
  response: Response;
  enduserName: string;
  enduserId: string;
  onGeneratePDFClick: () => void;
  onBack?: () => void;
  className?: string;
}

export const FormResponse = ({
  response,
  timestamp,
  onGeneratePDFClick,
  enduserId,
  onBack,
  className,
}: FormResponseProps) => {
  const { t } = useTranslation("forms");
  const [isSubmittingRelated, setIsSubmittingRelated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const closeFormSubmission = () => {
    if (isSubmittingRelated) {
      onBack?.();
    }
    setIsSubmittingRelated(false);
    setIsEditing(false);
  };

  const deleteMutation = useTellescopeMutation({
    model: "form_responses",
    method: "deleteOne",
    onSuccess: () => {
      onBack?.();
    },
  });

  const { isOverflowing, scrollRef, thumbRef, handleScroll } = useScrollSurface();

  if (isSubmittingRelated || isEditing) {
    return (
      <SinglePageFormSubmission
        onSubmit={closeFormSubmission}
        onSaveResponse={closeFormSubmission}
        onCancel={closeFormSubmission}
        contactId={enduserId}
        responseId={isEditing ? response.id : undefined}
        parentResponseId={isSubmittingRelated ? response.id : undefined}
        rootResponseId={isSubmittingRelated ? response.rootResponseId : undefined}
        formId={isEditing ? response.formId : undefined}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-4 h-full w-full", className)}>
      <div className="flex items-center justify-between">
        <div className="flex gap-1 items-center">
          {!!onBack && (
            <Button size="icon" variant="ghost" hoverLabel={t("Back")} onClick={onBack}>
              <ArrowLeft />
            </Button>
          )}
          <p className="text-sm text-muted-foreground">{formatDateTime(timestamp)}</p>
        </div>
        <div className="flex items-center">
          <DestructiveConfirmDialog onConfirm={() => deleteMutation.mutateAsync(response.id)}>
            <Button size="icon" onClick={() => {}} variant="ghost" hoverLabel={t("Delete Response")}>
              <Trash2 />
            </Button>
          </DestructiveConfirmDialog>
          <Button size="icon" onClick={onGeneratePDFClick} variant="ghost" hoverLabel={t("Generate PDF")}>
            <FileText />
          </Button>
          <Button size="icon" onClick={() => setIsEditing(true)} variant="ghost" hoverLabel={t("Edit Response")}>
            <Pencil />
          </Button>
          <Button
            size="icon"
            onClick={() => setIsSubmittingRelated(true)}
            variant="ghost"
            hoverLabel={t("Submit Related")}
          >
            <ClipboardPlus />
          </Button>
        </div>
      </div>

      <div className="relative flex flex-col flex-1 min-h-0 w-full">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={scrollSurfaceStyle({ faded: isOverflowing })}
          className={cn("flex flex-col gap-3 overflow-y-auto p-1 w-full", SCROLL_SURFACE_CLASS)}
        >
          {response.responses?.length ? (
            response.responses.map((item) => (
              <FormAnswer title={item.fieldTitle} answer={item.answer} key={item.fieldId} />
            ))
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <BlockEmptyState>{t("No response data available")}</BlockEmptyState>
            </div>
          )}
        </div>
        <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
      </div>
    </div>
  );
};
